import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Users, UserPlus, Wallet, TrendingUp, Coins, ShieldCheck, LifeBuoy, Banknote,
  Activity, ArrowLeft, AlertTriangle, CheckCircle2, Radio,
} from 'lucide-react'
import { PageHead } from '../AdminLayout'
import { Stat, Panel, LineChart, BarList, Select, Status, Tag, RefreshButton } from '../ui'
import { get } from '../../lib/api'
import { clsx, compact, fa, num, asArray} from '../../lib/format'

export default function Dashboard() {
  const [days, setDays] = useState('30')
  const [data, setData] = useState<any>(null)
  const [top, setTop] = useState<any[]>([])
  const [dist, setDist] = useState<any>(null)
  const [loading, setLoading] = useState(false)

  const load = () => {
    setLoading(true)
    Promise.all([
      get(`/admin/overview?days=${days}`).then(setData),
      get('/admin/top-users?metric=volume&limit=8').then((r: any) => setTop(asArray(r))),
      get('/admin/asset-distribution').then(setDist),
    ]).catch(() => {}).finally(() => setLoading(false))
  }
  useEffect(load, [days])

  const k = data?.kpi ?? {}
  const q = data?.queues ?? {}
  const h = data?.health ?? {}

  const queues = [
    { label: 'برداشت ریالی در انتظار', value: q.rialWithdrawals, to: '/admin/finance', icon: Banknote },
    { label: 'برداشت رمزارز در انتظار', value: q.chainWithdrawals, to: '/admin/finance', icon: Wallet },
    { label: 'واریز در حال تأیید', value: q.chainDeposits, to: '/admin/finance', icon: Activity },
    { label: 'احراز هویت در صف', value: q.kyc, to: '/admin/kyc', icon: ShieldCheck },
    { label: 'تیکت باز', value: q.tickets, to: '/admin/tickets', icon: LifeBuoy },
  ]

  return (
    <>
      <PageHead
        title="داشبورد مدیریت"
        subtitle="نمای کلی وضعیت سامانه، کاربران و گردش مالی"
        action={
          <div className="flex items-center gap-2">
            <Select value={days} onChange={setDays} options={[['7', '۷ روز'], ['30', '۳۰ روز'], ['90', '۹۰ روز']]} />
            <RefreshButton onClick={load} loading={loading} />
          </div>
        }
      />

      {/* هشدارهای وضعیت */}
      <div className="mb-5 flex flex-wrap gap-2">
        <Tag kind={h.maintenance ? 'down' : 'up'}>
          {h.maintenance ? 'حالت تعمیرات فعال است' : 'سرویس در حال اجرا'}
        </Tag>
        <Tag kind={h.tradingEnabled ? 'up' : 'down'}>
          معاملات {h.tradingEnabled ? 'فعال' : 'غیرفعال'}
        </Tag>
        <Tag kind={h.registrationOpen ? 'up' : 'gold'}>
          ثبت‌نام {h.registrationOpen ? 'باز' : 'بسته'}
        </Tag>
        <Tag kind="info">منبع قیمت: {h.priceSource === 'simulator' ? 'شبیه‌ساز' : h.priceSource}</Tag>
        <Tag kind={h.chain?.enabled ? (h.chain?.mainnet ? 'down' : 'gold') : 'muted'}>
          زنجیره: {h.chain?.enabled ? (h.chain?.mainnet ? 'مین‌نت' : 'تست‌نت') : 'غیرفعال'}
        </Tag>
      </div>

      {/* شاخص‌ها */}
      <div className="mb-5 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Stat label="کل کاربران" value={fa(k.usersTotal ?? 0)} icon={<Users className="h-5 w-5" />}
          hint={`${fa(k.usersToday ?? 0)} کاربر امروز · ${fa(k.usersWeek ?? 0)} این هفته`} />
        <Stat label="حجم معاملات ۲۴ ساعت" value={compact(k.volume24)} color="#22D3EE"
          icon={<TrendingUp className="h-5 w-5" />}
          hint={`${fa(k.trades24 ?? 0)} معامله`} />
        <Stat label="درآمد کارمزد (بازه)" value={compact(k.feeIncomeRange)} color="#00E39A"
          icon={<Coins className="h-5 w-5" />}
          hint={`${compact(k.feeIncome24)} در ۲۴ ساعت`} />
        <Stat label="دارایی نگهداری‌شده کاربران" value={compact(k.custody)} color="#FFB020"
          icon={<Wallet className="h-5 w-5" />}
          hint={`نرخ تتر: ${num(k.usdtIrt)} تومان`} />
      </div>

      {/* صف کارها */}
      <div className="mb-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        {queues.map((it) => (
          <Link key={it.label} to={it.to}>
            <div className={clsx('card flex items-center gap-3 p-3.5 transition hover:border-brand-500/40',
              it.value > 0 && 'ring-1 ring-gold/30')}>
              <span className={clsx('grid h-9 w-9 place-items-center rounded-lg',
                it.value > 0 ? 'bg-gold/15 text-gold' : 'bg-white/[.05] text-slate-500')}>
                <it.icon className="h-4 w-4" />
              </span>
              <div className="min-w-0">
                <div className="num text-lg font-bold text-white">{fa(it.value ?? 0)}</div>
                <div className="truncate text-[10px] text-slate-500">{it.label}</div>
              </div>
              <ArrowLeft className="mr-auto h-4 w-4 shrink-0 text-slate-600" />
            </div>
          </Link>
        ))}
      </div>

      {/* نمودارها */}
      <div className="mb-5 grid gap-4 lg:grid-cols-2">
        <Panel title="ثبت‌نام کاربران">
          <LineChart data={data?.charts?.signups ?? []} color="#7C5CFF" valueFormat={(v) => `${fa(v)} کاربر`} />
        </Panel>
        <Panel title="حجم معاملات (تومان)">
          <LineChart data={data?.charts?.volume ?? []} color="#22D3EE" valueFormat={compact} />
        </Panel>
        <Panel title="درآمد کارمزد (تومان)">
          <LineChart data={data?.charts?.fees ?? []} color="#00E39A" valueFormat={compact} />
        </Panel>
        <Panel title="واریز و برداشت (تومان)">
          <LineChart data={data?.charts?.deposits ?? []} color="#FFB020" valueFormat={compact} />
        </Panel>
      </div>

      {/* جداول کناری */}
      <div className="grid gap-4 lg:grid-cols-3">
        <Panel title="کاربران برتر بر اساس حجم">
          <div className="space-y-2">
            {top.map((u, i) => (
              <Link key={u.userId} to={`/admin/users/${u.userId}`}
                className="flex items-center gap-3 rounded-xl p-2 transition hover:bg-white/[.05]">
                <span className="num grid h-7 w-7 place-items-center rounded-full bg-brand-500/15 text-[11px] font-bold text-brand-200">
                  {fa(i + 1)}
                </span>
                <span className="num min-w-0 flex-1 truncate text-xs text-slate-200" dir="ltr">{u.email}</span>
                <span className="num text-xs text-slate-400">{compact(u.value)}</span>
              </Link>
            ))}
            {top.length === 0 && <p className="py-6 text-center text-xs text-slate-500">داده‌ای موجود نیست</p>}
          </div>
        </Panel>

        <Panel title="توزیع دارایی صرافی">
          <BarList
            items={(dist?.assets ?? []).slice(0, 8).map((a: any) => ({
              label: `${a.currency} · ${fa(a.holders)} دارنده`,
              value: a.valueIrt,
            }))}
            format={compact}
          />
        </Panel>

        <Panel title="سلامت شبکه‌های بلاک‌چین">
          <div className="space-y-2">
            {(h.chain?.networks ?? []).map((n: any) => (
              <div key={n.key} className="flex items-center gap-2 rounded-xl border border-white/[.06] bg-white/[.02] p-2.5">
                {n.active
                  ? <CheckCircle2 className="h-4 w-4 shrink-0 text-up" />
                  : <AlertTriangle className="h-4 w-4 shrink-0 text-down" />}
                <div className="min-w-0 flex-1">
                  <div className="text-xs text-slate-200">{n.faName}</div>
                  {n.error && <div className="truncate text-[10px] text-down">{n.error}</div>}
                </div>
                <span className="num text-[10px] text-slate-500">{fa(n.confirmations)} تأیید</span>
              </div>
            ))}
            {!(h.chain?.networks ?? []).length && (
              <p className="py-6 text-center text-xs text-slate-500">شبکه‌ای پیکربندی نشده است</p>
            )}
          </div>
        </Panel>
      </div>
    </>
  )
}
